import React, { useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import {
  Column,
  Row,
  Card,
  Flex,
  Box,
  Text,
  Button,
} from '../../../../design-system';
import { Form, Field } from 'react-final-form';
import { useEffectOnce } from 'react-use';
import { successRefetch, useData } from '../../../../helpers/data-loader';
import {
  isExpired,
  currencyFormatter,
  errorMsg,
} from '../../../../helpers/utils';
import Alert from '../../../../helpers/alert';
import AttachmentsBase from '../../../../components/Attachments';
import { ExpiredCard } from '../../../../components/Expired';
import FieldColumn from '../../../../components/FieldColumn';
import FieldText from '../../../../components/FieldText';
import CardHeader from '../../../../components/CardHeader';
import { AddMemberCollapsibleRowTable } from '../../../../components/AddMemberTable';
import {
  BOARD_MEMBER,
  STAFF_MEMBER,
  BOARD_MEMBER_MODAL_FIELDS,
  STAFF_MEMBER_MODAL_FIELDS,
  PRIMARY_SECONDARY_BOARD_MEMBER_COLUMNS,
  PRIMARY_SECONDARY_STAFF_MEMBER_COLUMNS,
} from './constants';
import { ERR_MSG } from '../../../../globalConstants';
import DateField from '../../../../components/DateField';

const Attachments = AttachmentsBase as React.ComponentType<any>;

const UnderlinedText = styled(Text)`
  text-decoration: underline;
`;

interface GovernanceStaffProps {
  data: { id: any; governance: any; all_files?: any[]; updated_at?: any };
  refetch: () => void;
  canEdit: boolean;
}

const GovernanceStaff = ({
  data: { id, governance: rawGovernance, all_files, updated_at: updatedAt },
  refetch,
  canEdit,
}: GovernanceStaffProps) => {
  const governance = rawGovernance ?? ({} as any);
  const members: any[] = Array.isArray(governance.members)
    ? governance.members
    : [];
  const [isEditing, setIsEditing] = useState(false);
  const [targetedBoardMember, setTargetedBoardMember] = useState<any>('');
  const [targetedStaffMember, setTargetedStaffMember] = useState<any>('');
  const [isOpenAddBoard, setIsOpenAddBoard] = useState(false);
  const [isOpenEditBoard, setIsOpenEditBoard] = useState(false);
  const [isOpenAddStaff, setIsOpenAddStaff] = useState(false);
  const [isOpenEditStaff, setIsOpenEditStaff] = useState(false);
  const [isOpenAttachments, setIsOpenAttachments] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState();
  const [email, setEmail] = useState();

  const [contacts, fetchContacts] = useData('/contact', {
    errorMsg: errorMsg('contacts'),
  });

  useEffectOnce(() => {
    fetchContacts();
  });

  const clearPhoneAndEmail = () => {
    setPhone(undefined);
    setEmail(undefined);
  };

  const clearState = () => {
    clearPhoneAndEmail();
    setName('');
  };

  // Board
  const toggleAddBoard = () => {
    setIsOpenAddBoard(!isOpenAddBoard);
    clearState();
  };

  const closeEditBoard = () => {
    setIsOpenEditBoard(false);
    setTargetedBoardMember('');
    clearState();
  };

  const openBoardEditModal = async (values) => {
    setTargetedBoardMember(values);
    setIsOpenEditBoard(true);
    setName(values.name);
    setPhone(values.phone);
    setEmail(values.email);
  };

  // Staff
  const toggleAddStaff = () => {
    setIsOpenAddStaff(!isOpenAddStaff);
    clearState();
  };

  const closeEditStaff = () => {
    setIsOpenEditStaff(false);
    setTargetedStaffMember('');
    clearState();
  };

  const openStaffEditModal = async (values) => {
    setTargetedStaffMember(values);
    setIsOpenEditStaff(true);
    setName(values.name);
    setEmail(values.email);
    setPhone(values.phone);
  };

  // Attachment
  const toggleAttachments = () => {
    setIsOpenAttachments(!isOpenAttachments);
  };

  const handleGovernanceUpdate = async (values) => {
    try {
      if (!governance?.id) {
        Alert.error(ERR_MSG);
        return;
      }
      await axios.put(`/client/${id}/governance/${governance.id}`, {
        ...values,
        board_giving_currency:
          values.board_giving_currency &&
          values.board_giving_currency.replace(/[^0-9.]/g, ''),
      });
      successRefetch(refetch);
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };

  const handleAddMember = async (values, type) => {
    try {
      if (!governance?.id) {
        Alert.error(ERR_MSG);
        return;
      }
      await axios.post(`/client/${id}/governance/${governance.id}/member`, {
        ...values,
        name,
        phone,
        email,
        type,
      });
      successRefetch(refetch);
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };

  const handleDeleteMember = async (member_id) => {
    try {
      if (!governance?.id) {
        Alert.error(ERR_MSG);
        return;
      }
      await axios.delete(
        `/client/${id}/governance/${governance.id}/member/${member_id}`,
      );
      successRefetch(refetch);
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };

  const handleUpdateMember = async (values) => {
    try {
      if (!governance?.id) {
        Alert.error(ERR_MSG);
        return;
      }
      await axios.put(
        `/client/${id}/governance/${governance.id}/member/${values.id}`,
        { ...values, name, phone, email },
      );
      successRefetch(refetch);
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };

  const selectedContact = contacts.find(
    (contact) => contact.full_name === name,
  );

  const emailOptions =
    selectedContact &&
    selectedContact.emails.map(({ value }) => ({
      label: value,
      value,
    }));

  const phoneOptions =
    selectedContact &&
    selectedContact.phones.map(({ value }) => ({
      label: value,
      value,
    }));

  const nameOptions = contacts.map(({ full_name }) => ({
    label: full_name,
    value: full_name,
  }));

  const updateFieldsWithContacts = (field) => {
    switch (field.name) {
      case 'name':
        return {
          ...field,
          options: [
            { label: '+ New Contact', value: 'addnew' },
            ...(nameOptions || []),
          ],
          onChange: (val) => {
            setName(val);
            clearPhoneAndEmail();
          },
          key: `name${name}`,
        };
      case 'email':
        return {
          ...field,
          disabled: !name,
          value: email,
          onChange: (val) => setEmail(val),
          key: `email${email}`,
          options: emailOptions,
        };
      case 'phone':
        return {
          ...field,
          disabled: !name,
          value: phone,
          key: `phone${phone}`,
          onChange: (val) => setPhone(val),
          options: phoneOptions,
        };
      default:
        return field;
    }
  };

  const boardMemberModalFields = BOARD_MEMBER_MODAL_FIELDS.map(
    updateFieldsWithContacts,
  );

  const staffMemeberModalFields = STAFF_MEMBER_MODAL_FIELDS.map(
    updateFieldsWithContacts,
  );

  return (
    <>
      <ExpiredCard expired={isExpired(governance?.expires)}>
        <CardHeader
          title="Governance and Staff"
          onEditChange={setIsEditing}
          isEditing={isEditing}
          canEdit={canEdit}
          updatedAt={updatedAt}
        />
        <Card.Body>
          {isEditing ? (
            <Form
              onSubmit={handleGovernanceUpdate}
              initialValues={{
                ...governance,
                board_giving_currency:
                  governance.board_giving_currency &&
                  currencyFormatter.format(governance.board_giving_currency),
              }}
              render={({ handleSubmit, submitting, pristine }) => (
                <form onSubmit={handleSubmit}>
                  <Flex mt={15}>
                    <Box mr={6}>
                      <UnderlinedText fontSize={18}>
                        {BOARD_MEMBER.TITLE}
                      </UnderlinedText>
                      <Row mt={20}>
                        {BOARD_MEMBER.FORM_FIELDS.map((field) => {
                          if (field.type === 'date') {
                            return (
                              <Column key={field.name} pb={2} width={300}>
                                <Field name={field.name}>
                                  {({ input }) => {
                                    return (
                                      <DateField
                                        name={field.name}
                                        label={field.label}
                                        selected={input.value}
                                        onChange={(value) =>
                                          input.onChange(value)
                                        }
                                        showPastDue={false}
                                      />
                                    );
                                  }}
                                </Field>
                              </Column>
                            );
                          }

                          return (
                            <FieldColumn
                              key={field.name}
                              width={250}
                              {...field}
                            />
                          );
                        })}
                      </Row>
                    </Box>
                    <Box mr={6}>
                      <UnderlinedText fontSize={18}>
                        {STAFF_MEMBER.TITLE}
                      </UnderlinedText>
                      <Row mt={20}>
                        {STAFF_MEMBER.FORM_FIELDS.map((field) => {
                          if (field.type === 'date') {
                            return (
                              <Column key={field.name} pb={2} width={300}>
                                <Field name={field.name}>
                                  {({ input }) => {
                                    return (
                                      <DateField
                                        name={field.name}
                                        label={field.label}
                                        selected={input.value}
                                        onChange={(value) =>
                                          input.onChange(value)
                                        }
                                        showPastDue={false}
                                      />
                                    );
                                  }}
                                </Field>
                              </Column>
                            );
                          }

                          return (
                            <FieldColumn
                              key={field.name}
                              width={250}
                              {...field}
                            />
                          );
                        })}
                      </Row>
                    </Box>
                  </Flex>
                  <Button.Group justifyContent="flex-end">
                    <Button
                      id="exit-edit-button"
                      mb={30}
                      type="submit"
                      disabled={submitting || pristine}
                    >
                      Save
                    </Button>
                  </Button.Group>
                </form>
              )}
            />
          ) : (
            <>
              <Flex mb={30}>
                <Box mr={5}>
                  <UnderlinedText fontSize={18} mb={3}>
                    {BOARD_MEMBER.TITLE}
                  </UnderlinedText>
                  <Row>
                    {BOARD_MEMBER.FORM_FIELDS.map((field) => (
                      <Column key={field.name} mt={2} pb={3} width={300}>
                        <FieldText data={governance} {...field} />
                      </Column>
                    ))}
                  </Row>
                </Box>
                <Box>
                  <UnderlinedText fontSize={18} mb={3}>
                    {STAFF_MEMBER.TITLE}
                  </UnderlinedText>

                  <Row>
                    {STAFF_MEMBER.FORM_FIELDS.map((field) => (
                      <Column key={field.name} mt={2} pb={3} width={200}>
                        <FieldText
                          key={field.name}
                          data={governance}
                          {...field}
                        />
                      </Column>
                    ))}
                  </Row>
                </Box>
              </Flex>
              <Row>
                <Column mt={2} pb={3} width={200} mb={30}>
                  <FieldText
                    name="expires"
                    label="Expiration Date"
                    data={governance}
                    type="date"
                  />
                </Column>
              </Row>
            </>
          )}
        </Card.Body>
      </ExpiredCard>
      <Card mt={20}>
        <AddMemberCollapsibleRowTable
          name="Board Members"
          data={members
            .filter((member) => member.type === 'Board')
            .map((item) => ({
              ...item,
              handleDeleteMember,
              openBoardEditModal,
              canEdit,
            }))}
          columns={PRIMARY_SECONDARY_BOARD_MEMBER_COLUMNS as any}
          onSubmit={(values) => handleAddMember(values, 'Board')}
          isOpenAdd={isOpenAddBoard}
          isOpenEdit={isOpenEditBoard}
          fields={boardMemberModalFields}
          onAdd={toggleAddBoard}
          row={{ ...targetedBoardMember }}
          onCloseAdd={toggleAddBoard}
          onCloseEdit={closeEditBoard}
          handleUpdateMember={handleUpdateMember}
          canEdit={canEdit}
        />
      </Card>
      <Card mt={20}>
        <AddMemberCollapsibleRowTable
          name="Staff Members"
          data={members
            .filter((member) => member.type === 'Staff')
            .map((item) => ({
              ...item,
              handleDeleteMember,
              openStaffEditModal,
              canEdit,
            }))}
          columns={PRIMARY_SECONDARY_STAFF_MEMBER_COLUMNS as any}
          onSubmit={(values) => handleAddMember(values, 'Staff')}
          isOpenAdd={isOpenAddStaff}
          isOpenEdit={isOpenEditStaff}
          fields={staffMemeberModalFields}
          onAdd={toggleAddStaff}
          row={{ ...targetedStaffMember }}
          onCloseAdd={toggleAddStaff}
          onCloseEdit={closeEditStaff}
          handleUpdateMember={handleUpdateMember}
          canEdit={canEdit}
        />
      </Card>
      <Attachments
        data={
          all_files
            ? all_files.filter(
                ({ model_type }) => model_type === 'VaultGovernance',
              )
            : []
        }
        canEdit={canEdit}
        isModalOpen={isOpenAttachments}
        onClick={toggleAttachments}
        onClose={toggleAttachments}
        modelId={id}
        modelType="VaultGovernance"
        clientId={id}
        refetch={refetch}
        maxHeight="15rem"
        disabled={!canEdit}
      />
    </>
  );
};

export default GovernanceStaff;
